/**
 * Daily macro targets that always add up: calories = 4 x protein + 4 x carbs + 9 x fat.
 *
 * Three ways to set them:
 *  - grams: type protein, carbs and fat; the calories follow.
 *  - percent: type calories and each macro's share (the shares must total 100%).
 *  - protein_fat_first: type calories, protein (grams or per body weight / lean mass) and fat
 *    (grams or % of calories); carbs get whatever is left.
 * Grams are rounded to 0.1 g (what the profile stores) and the calorie target is re-derived from
 * the rounded grams, so the two can never disagree by more than half a calorie.
 *
 * Reference ranges shown next to the numbers (information, never errors):
 *  - Acceptable Macronutrient Distribution Ranges for adults: protein 10-35%, fat 20-35%,
 *    carbohydrate 45-65% of energy. Institute of Medicine. Dietary Reference Intakes for Energy,
 *    Carbohydrate, Fiber, Fat, Fatty Acids, Cholesterol, Protein, and Amino Acids. 2005.
 *    doi:10.17226/10490.
 */
import { KCAL_PER_G } from './nutrition';
import type { MacroPlan, ProteinBasis } from './types';

export const KG_PER_LB: number = 0.45359237;

export interface MacroTargets {
  calories: number;
  protein_g: number;
  carbs_g: number;
  fat_g: number;
}

export interface MacroShares { protein: number; carbs: number; fat: number }

export type MacroPlanResult = { ok: true; targets: MacroTargets; shares: MacroShares; notes: string[] } | { ok: false; error: string };

export interface BodyForProtein { weightKg: number | null; leanMassKg: number | null }

const r1 = (x: number): number => Math.round(x * 10) / 10;
const finite = (x: number | undefined): x is number => typeof x === 'number' && Number.isFinite(x);

/** Calories from grams (Atwater 4/4/9). */
export const kcalFromMacros = (p: number, c: number, f: number): number => p * KCAL_PER_G.protein + c * KCAL_PER_G.carbs + f * KCAL_PER_G.fat;

export function sharesOf(t: Pick<MacroTargets, 'protein_g' | 'carbs_g' | 'fat_g'>): MacroShares {
  const p: number = t.protein_g * KCAL_PER_G.protein;
  const c: number = t.carbs_g * KCAL_PER_G.carbs;
  const f: number = t.fat_g * KCAL_PER_G.fat;
  const total: number = p + c + f;
  return total > 0 ? { protein: p / total, carbs: c / total, fat: f / total } : { protein: 0, carbs: 0, fat: 0 };
}

export const PROTEIN_BASIS_LABEL: Readonly<Record<ProteinBasis, string>> = {
  g: 'grams a day',
  g_per_kg: 'g per kg of body weight',
  g_per_lb: 'g per lb of body weight',
  g_per_kg_lean: 'g per kg of lean mass',
  g_per_lb_lean: 'g per lb of lean mass',
};

/** Protein grams for an amount in one of the bases, or an error when the body data is missing. */
export function proteinGrams(amount: number, basis: ProteinBasis, body: BodyForProtein): number | string {
  switch (basis) {
    case 'g': return amount;
    case 'g_per_kg': return body.weightKg === null ? 'Log a weigh-in to set protein per body weight.' : amount * body.weightKg;
    case 'g_per_lb': return body.weightKg === null ? 'Log a weigh-in to set protein per body weight.' : amount * (body.weightKg / KG_PER_LB);
    case 'g_per_kg_lean': return body.leanMassKg === null ? 'Add a body-fat or lean-mass measurement to set protein per lean mass.' : amount * body.leanMassKg;
    case 'g_per_lb_lean': return body.leanMassKg === null ? 'Add a body-fat or lean-mass measurement to set protein per lean mass.' : amount * (body.leanMassKg / KG_PER_LB);
  }
}

function finish(p: number, c: number, f: number): MacroPlanResult {
  const protein_g: number = r1(p);
  const carbs_g: number = r1(c);
  const fat_g: number = r1(f);
  if (protein_g < 0 || carbs_g < 0 || fat_g < 0) return { ok: false, error: 'Macros can’t be negative.' };
  if (protein_g > 1000 || fat_g > 1000 || carbs_g > 2000) return { ok: false, error: 'Those grams are beyond what the app can store (protein and fat up to 1,000 g, carbs up to 2,000 g).' };
  const calories: number = Math.round(kcalFromMacros(protein_g, carbs_g, fat_g));
  if (calories < 800 || calories > 10000) return { ok: false, error: `That comes to ${calories.toLocaleString()} kcal; the target has to be between 800 and 10,000.` };
  const targets: MacroTargets = { calories, protein_g, carbs_g, fat_g };
  const shares: MacroShares = sharesOf(targets);
  return { ok: true, targets, shares, notes: rangeNotes(shares) };
}

/** Plain-language notes when a share sits outside the adult reference ranges (IOM 2005). */
export function rangeNotes(s: MacroShares): string[] {
  const notes: string[] = [];
  const pct = (x: number): string => `${Math.round(x * 100)}%`;
  if (s.protein > 0.35) notes.push(`Protein is ${pct(s.protein)} of calories, above the 10-35% reference range. Common for lifters cutting; make sure it's on purpose.`);
  if (s.protein < 0.10) notes.push(`Protein is ${pct(s.protein)} of calories, below the 10-35% reference range.`);
  if (s.fat < 0.20) notes.push(`Fat is ${pct(s.fat)} of calories, below the 20-35% reference range for adults.`);
  if (s.fat > 0.35) notes.push(`Fat is ${pct(s.fat)} of calories, above the 20-35% reference range (fine for low-carb eating).`);
  return notes;
}

export function planMacros(plan: MacroPlan, body: BodyForProtein): MacroPlanResult {
  if (plan.mode === 'grams') {
    if (!finite(plan.protein_g) || !finite(plan.carbs_g) || !finite(plan.fat_g)) return { ok: false, error: 'Enter protein, carbs and fat in grams.' };
    return finish(plan.protein_g, plan.carbs_g, plan.fat_g);
  }

  const kcal: number | undefined = plan.calories;
  if (!finite(kcal) || kcal < 800 || kcal > 10000) return { ok: false, error: 'Enter a calorie target between 800 and 10,000.' };

  if (plan.mode === 'percent') {
    const p: number | undefined = plan.protein_pct;
    const c: number | undefined = plan.carbs_pct;
    const f: number | undefined = plan.fat_pct;
    if (!finite(p) || !finite(c) || !finite(f)) return { ok: false, error: 'Enter a percentage for protein, carbs and fat.' };
    if (p < 0 || c < 0 || f < 0) return { ok: false, error: 'Percentages can’t be negative.' };
    const sum: number = p + c + f;
    if (Math.abs(sum - 100) > 0.5) return { ok: false, error: `The percentages add up to ${Math.round(sum * 10) / 10}%. They need to total 100%.` };
    return finish((kcal * p) / 100 / KCAL_PER_G.protein, (kcal * c) / 100 / KCAL_PER_G.carbs, (kcal * f) / 100 / KCAL_PER_G.fat);
  }

  // protein_fat_first
  if (!finite(plan.protein_amount) || plan.protein_amount < 0) return { ok: false, error: 'Enter your protein target.' };
  const protein: number | string = proteinGrams(plan.protein_amount, plan.protein_basis ?? 'g', body);
  if (typeof protein === 'string') return { ok: false, error: protein };
  if (!finite(plan.fat_amount) || plan.fat_amount < 0) return { ok: false, error: 'Enter your fat target.' };
  const fat: number = (plan.fat_basis ?? 'g') === 'pct' ? (kcal * plan.fat_amount) / 100 / KCAL_PER_G.fat : plan.fat_amount;
  const pRounded: number = r1(protein);
  const fRounded: number = r1(fat);
  const left: number = kcal - kcalFromMacros(pRounded, 0, fRounded);
  if (left < 0) {
    return { ok: false, error: `Protein and fat alone come to ${Math.round(kcal - left).toLocaleString()} kcal, more than your ${Math.round(kcal).toLocaleString()} kcal target. Lower one of them or raise the calories.` };
  }
  return finish(pRounded, left / KCAL_PER_G.carbs, fRounded);
}

/**
 * Keeps the plan's intent but moves it to a new calorie target (e.g. the recommended intake for
 * the goal): percentages stay; in the other modes protein and fat stay and carbs take the change.
 */
export function withCalories(plan: MacroPlan | null, calories: number, current: Pick<MacroTargets, 'protein_g' | 'fat_g'> | null): MacroPlan {
  const kcal: number = Math.round(calories);
  if (plan?.mode === 'percent') return { ...plan, calories: kcal };
  if (plan?.mode === 'protein_fat_first') return { ...plan, calories: kcal };
  const protein: number = plan?.protein_g ?? current?.protein_g ?? r1((kcal * 0.3) / KCAL_PER_G.protein);
  const fat: number = plan?.fat_g ?? current?.fat_g ?? r1((kcal * 0.3) / KCAL_PER_G.fat);
  return { mode: 'protein_fat_first', calories: kcal, protein_amount: protein, protein_basis: 'g', fat_amount: fat, fat_basis: 'g' };
}

/** A starting plan built from the profile's saved targets. */
export function planFromTargets(t: { target_calories: number | null; target_protein_g: number | null; target_carbs_g: number | null; target_fat_g: number | null }): MacroPlan {
  if (t.target_protein_g !== null && t.target_carbs_g !== null && t.target_fat_g !== null) {
    return { mode: 'grams', protein_g: t.target_protein_g, carbs_g: t.target_carbs_g, fat_g: t.target_fat_g };
  }
  return { mode: 'percent', calories: t.target_calories ?? 2000, protein_pct: 30, carbs_pct: 40, fat_pct: 30 };
}
